import { Button } from '@startuptools/ui/Button';
import { Dialog } from '@startuptools/ui/Dialog';
import { DialogActions, DialogContent, DialogTitle } from '@startuptools/ui/Dialog';
import { GenericError } from '@startuptools/ui/Error';
import { AccordionDetails } from '@startuptools/ui/AccordionDetails';
import { Typography } from '@startuptools/ui/Typography';
import { Alert } from '@startuptools/ui/Alert';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Box } from '@startuptools/ui/Box';
import { AccordionGroup } from '@startuptools/ui/AccordionGroup';
import { Accordion } from '@startuptools/ui/Accordion';
import { AccordionSummary } from '@startuptools/ui/AccordionSummary';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useLingui } from '@lingui/react';
import { type GqlCapTable } from '../../../graphql/react-operations';
import { Switch } from '@startuptools/ui/joy/Switch';
import { DateInput } from '@startuptools/ui/custom/DateInput';
import { DateTime } from 'luxon';
import { InfoTooltip } from '@startuptools/ui/InfoTooltip';

type CapTableEdit = Pick<GqlCapTable, 'displayName' | 'pricePerShare' | 'unregisteredShares' | 'fullyDiluted'> & {
  recordDate: DateTime | null;
};

interface Props {
  onCancel: () => void;
  onSubmit: (data: CapTableEdit) => void;
  defaultValues?: CapTableEdit;
  loading?: boolean;
  mode: 'edit' | 'create';
  error: boolean;
  hasWise?: boolean;
}

export const CapTableForm = ({ error, onCancel, defaultValues, onSubmit, mode, hasWise, loading = false }: Props) => {
  const { i18n } = useLingui();
  const { control, register, handleSubmit, watch } = useForm<CapTableEdit>({ defaultValues });
  return (
    <Dialog>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} maxWidth="420px">
        <DialogTitle onClose={onCancel}>
          {mode === 'edit' ? <Trans id="Edit CapTable" /> : <Trans id="Create CapTable" />}
        </DialogTitle>

        <DialogContent>
          {hasWise && mode === 'edit' && (
            <Alert>
              <Trans id="This CapTable contains WISE convertibles. Since the future share price affects how many shares a WISE converts to, updating share price will trigger a refresh of the CapTable." />
            </Alert>
          )}
          <FormControl>
            <FormLabel>
              <Trans id="Name" />
            </FormLabel>
            <Input {...register('displayName', { required: true })} />
          </FormControl>

          <Controller
            control={control}
            name="pricePerShare"
            render={({ field }) => (
              <FormControl>
                <FormLabel>
                  <Trans id="Price per share" />
                </FormLabel>
                <NumericFormatInput {...field} value={field.value ?? ''} lang={i18n.locale} />
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="fullyDiluted"
            render={({ field }) => (
              <FormControl>
                <FormLabel sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Trans id="Fully diluted" />
                  <InfoTooltip>
                    <Trans id="Include all outstanding options in the total share count." />
                  </InfoTooltip>
                </FormLabel>
                <Switch
                  sx={{ alignSelf: 'flex-start', mt: 2 }}
                  startDecorator={
                    <Typography level="body-sm">
                      <Trans id="Off" comment="opposite of on" />
                    </Typography>
                  }
                  endDecorator={
                    <Typography level="body-sm">
                      <Trans id="On" />
                    </Typography>
                  }
                  checked={field.value}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => field.onChange(event.target.checked)}
                />
              </FormControl>
            )}
          />
          {!watch('fullyDiluted') && (
            <Controller
              name="recordDate"
              control={control}
              disabled={loading}
              render={({ field }) => (
                <FormControl sx={{ maxWidth: 250 }}>
                  <FormLabel sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Trans id="Date" />
                    <InfoTooltip>
                      <Trans id="Keep empty to always have the Cap Table reflect the current date, even in the future." />
                    </InfoTooltip>
                  </FormLabel>

                  <DateInput {...field} slotProps={{ field: { clearable: true } }} />
                </FormControl>
              )}
            />
          )}

          {hasWise && (
            <>
              <AccordionGroup variant="soft">
                <Accordion>
                  <AccordionSummary>
                    <Typography level="title-md">
                      <Trans id="WISE settings" />
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Alert>
                      <Trans id="Are there any shares that will be registered before the next investment round? This could e.g. be QESOs." />
                    </Alert>
                    <Controller
                      control={control}
                      name="unregisteredShares"
                      render={({ field }) => (
                        <FormControl>
                          <FormLabel>
                            <Trans id="Unregistered shares" />
                          </FormLabel>
                          <NumericFormatInput {...field} value={field.value ?? ''} lang={i18n.locale} />
                        </FormControl>
                      )}
                    />
                  </AccordionDetails>
                </Accordion>
              </AccordionGroup>
            </>
          )}
          {error && <GenericError />}
        </DialogContent>
        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onCancel}>
            <Trans id="Cancel" />
          </Button>
          <Button type="submit" loading={loading}>
            <Trans id="Save" />
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
